/**
 * Skafe Freight Calculator Web Component
 *
 * Implements real-time commercial shipping calculation for Brazilian CEPs
 * using the Shopify Ajax Cart shipping rates API (/cart/prepare_shipping_rates.json)
 * with offline CEP-range fallback, canonical rates configuration,
 * full Liquid localization (data-i18n), selectable rate rows, and an estimated total breakdown.
 */

(function () {
  'use strict';

  if (customElements.get('skafe-freight-calculator')) return;

  const CEP_UF_RANGES = [
    { min: 1000000, max: 19999999, uf: 'SP', state: 'São Paulo' },
    { min: 20000000, max: 28999999, uf: 'RJ', state: 'Rio de Janeiro' },
    { min: 29000000, max: 29999999, uf: 'ES', state: 'Espírito Santo' },
    { min: 30000000, max: 39999999, uf: 'MG', state: 'Minas Gerais' },
    { min: 40000000, max: 48999999, uf: 'BA', state: 'Bahia' },
    { min: 49000000, max: 49999999, uf: 'SE', state: 'Sergipe' },
    { min: 50000000, max: 56999999, uf: 'PE', state: 'Pernambuco' },
    { min: 57000000, max: 57999999, uf: 'AL', state: 'Alagoas' },
    { min: 58000000, max: 58999999, uf: 'PB', state: 'Paraíba' },
    { min: 59000000, max: 59999999, uf: 'RN', state: 'Rio Grande do Norte' },
    { min: 60000000, max: 63999999, uf: 'CE', state: 'Ceará' },
    { min: 64000000, max: 64999999, uf: 'PI', state: 'Piauí' },
    { min: 65000000, max: 65999999, uf: 'MA', state: 'Maranhão' },
    { min: 66000000, max: 68899999, uf: 'PA', state: 'Pará' },
    { min: 68900000, max: 68999999, uf: 'AP', state: 'Amapá' },
    { min: 69000000, max: 69299999, uf: 'AM', state: 'Amazonas' },
    { min: 69300000, max: 69399999, uf: 'RR', state: 'Roraima' },
    { min: 69400000, max: 69899999, uf: 'AM', state: 'Amazonas' },
    { min: 69900000, max: 69999999, uf: 'AC', state: 'Acre' },
    { min: 70000000, max: 72799999, uf: 'DF', state: 'Distrito Federal' },
    { min: 72800000, max: 72999999, uf: 'GO', state: 'Goiás' },
    { min: 73000000, max: 73699999, uf: 'DF', state: 'Distrito Federal' },
    { min: 73700000, max: 76799999, uf: 'GO', state: 'Goiás' },
    { min: 76800000, max: 76999999, uf: 'RO', state: 'Rondônia' },
    { min: 77000000, max: 77999999, uf: 'TO', state: 'Tocantins' },
    { min: 78000000, max: 78899999, uf: 'MT', state: 'Mato Grosso' },
    { min: 79000000, max: 79999999, uf: 'MS', state: 'Mato Grosso do Sul' },
    { min: 80000000, max: 87999999, uf: 'PR', state: 'Paraná' },
    { min: 88000000, max: 89999999, uf: 'SC', state: 'Santa Catarina' },
    { min: 90000000, max: 99999999, uf: 'RS', state: 'Rio Grande do Sul' },
  ];

  function getUfFromCep(cleanCep) {
    const num = parseInt(cleanCep, 10);
    if (isNaN(num)) return null;
    const found = CEP_UF_RANGES.find((r) => num >= r.min && num <= r.max);
    return found || null;
  }

  function formatCep(value) {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  class SkafeFreightCalculator extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.button = null;
      this.resultContainer = null;
      this.canonicalRates = null;
      this.i18n = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input[name="cep"]');
      this.button = this.querySelector('.freight-calculator__button');
      this.resultContainer = this.querySelector('.freight-calculator__result');

      const i18nEl = this.querySelector('script[type="application/json"][data-i18n]');
      if (i18nEl) {
        try {
          this.i18n = JSON.parse(i18nEl.textContent);
        } catch (e) {}
      }

      const ratesEl = this.querySelector('script[type="application/json"][data-rates]');
      if (ratesEl) {
        try {
          this.canonicalRates = JSON.parse(ratesEl.textContent);
        } catch (e) {}
      }

      if (this.input) {
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.calculate();
          }
        });

        // Restore cached CEP
        try {
          const cachedCep = sessionStorage.getItem('skafe_shipping_cep') || localStorage.getItem('skafe_shipping_cep');
          if (cachedCep && !this.input.value) {
            this.input.value = formatCep(cachedCep);
          }
        } catch (e) {}

        // Theme setting: re-estimate with the last CEP (the drawer re-renders on every cart change).
        if (this.dataset.autoCalculate === 'true' && this.input.value.replace(/\D/g, '').length === 8) {
          this.calculate();
        }
      }

      if (this.button) {
        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.calculate();
        });
      }

      if (this.resultContainer) {
        this.resultContainer.addEventListener('change', (e) => {
          if (e.target.matches('.freight-result__radio')) this.selectRate(this.rates[e.target.value]);
        });
        this.resultContainer.addEventListener('click', (e) => {
          if (!e.target.closest('.freight-result__change') || !this.input) return;
          this.input.focus();
          this.input.select();
        });
      }
    }

    handleInput(e) {
      const formatted = formatCep(e.target.value);
      e.target.value = formatted;
      if (this.resultContainer) {
        this.resultContainer.innerHTML = '';
        this.resultContainer.hidden = true;
      }
      this.resetTotals();
    }

    showStatus(message, isError = false) {
      if (!this.resultContainer) return;
      this.resultContainer.hidden = false;
      this.resultContainer.innerHTML = `
        <div class="freight-result__status ${isError ? 'freight-result__status--error' : 'freight-result__status--loading'}">
          ${isError ? '<span class="freight-result__icon">⚠️</span>' : '<span class="freight-result__spinner"></span>'}
          <span>${message}</span>
        </div>
      `;
      if (isError) {
        this.resetTotals();
      }
    }

    // Reads the server-rendered total once (kept in data-original-text) so repeated picks never compound freight.
    updateTotalsWithFreight(freightPrice, rateName) {
      const subtotalLabel = this.i18n?.subtotal || 'Subtotal';
      const shippingLabel = (this.i18n?.shipping_line || 'Frete [method]').replace(
        '[method]',
        rateName || this.i18n?.default_method || 'Entrega padrão'
      );

      document.querySelectorAll('.totals').forEach((totals) => {
        const valEl = totals.querySelector('.totals__total-value');
        if (!valEl) return;
        if (!valEl.dataset.originalText) valEl.dataset.originalText = valEl.textContent.trim();

        const match = valEl.dataset.originalText.match(/[\d.,]+/);
        const subtotal = match ? parseFloat(match[0].replace(/\./g, '').replace(',', '.')) || 0 : 0;
        if (subtotal <= 0) return;

        valEl.textContent = formatCurrency(subtotal + freightPrice);

        let breakdown = totals.previousElementSibling;
        if (!breakdown?.matches('.totals__freight-breakdown')) {
          breakdown = document.createElement('dl');
          breakdown.className = 'totals__freight-breakdown';
          totals.insertAdjacentElement('beforebegin', breakdown);
        }
        breakdown.innerHTML = `
          <div><dt>${subtotalLabel}</dt><dd>${formatCurrency(subtotal)}</dd></div>
          <div><dt>${escapeHtml(shippingLabel)}</dt><dd>${formatCurrency(freightPrice)}</dd></div>
        `;
      });
    }

    resetTotals() {
      document.querySelectorAll('.totals').forEach((totals) => {
        const valEl = totals.querySelector('.totals__total-value');
        if (valEl?.dataset.originalText) valEl.textContent = valEl.dataset.originalText;

        const breakdown = totals.previousElementSibling;
        if (breakdown?.matches('.totals__freight-breakdown')) breakdown.remove();
      });
    }

    // Shopify's delivery_days are business days: [min, max].
    formatEta(days) {
      const [min, max = min] = (days || []).map(Number).filter((n) => n > 0);
      if (!max) return '';
      if (max === 1) return this.i18n?.eta_one || 'Entrega em 1 dia útil';
      if (min === max) {
        return (this.i18n?.eta_until || 'Entrega em até [days] dias úteis').replace('[days]', max);
      }
      return (this.i18n?.eta_range || 'Entrega em [min] a [max] dias úteis').replace('[min]', min).replace('[max]', max);
    }

    showResult(rates, locationLabel) {
      if (!this.resultContainer) return;
      this.resultContainer.hidden = false;

      const defaultName = this.i18n?.default_method || 'Entrega padrão';

      // Cheapest first, and it is the default pick unless the customer already chose another for this CEP.
      this.rates = rates.map((r) => ({ ...r, price: Number(r.price) })).sort((a, b) => a.price - b.price);
      let saved = '';
      try {
        saved = sessionStorage.getItem('skafe_shipping_rate') || '';
      } catch (e) {}
      const selected = this.rates.find((r) => saved === `${this.cleanCep}:${r.name}`) || this.rates[0];

      const renderRate = (rate) => {
        const eta = this.dataset.showEta === 'false' ? '' : this.formatEta(rate.days);
        return `
        <label class="freight-result__option">
          <input type="radio" class="freight-result__radio" name="freight-rate-${this.dataset.context}" value="${this.rates.indexOf(rate)}"${rate === selected ? ' checked' : ''}>
          <span class="freight-result__option-main">
            ${eta ? `<span class="freight-result__option-eta">${eta}</span>` : ''}
            <span class="freight-result__option-name">${escapeHtml(rate.name || defaultName)}</span>
          </span>
          <strong class="freight-result__option-price">${formatCurrency(rate.price)}</strong>
        </label>
      `;
      };

      const visibleCount = parseInt(this.dataset.visibleRates, 10) || 3;
      const extra = this.rates.slice(visibleCount);
      const moreLabel = (this.i18n?.show_more || 'Ver mais [count] opções').replace('[count]', extra.length);
      const lessLabel = this.i18n?.show_less || 'Ver menos';
      const moreHtml = extra.length
        ? `<details class="freight-result__more"${extra.includes(selected) ? ' open' : ''}>
            <summary><span class="freight-result__more-label">${moreLabel}</span><span class="freight-result__less-label">${lessLabel}</span></summary>
            ${extra.map(renderRate).join('')}
          </details>`
        : '';

      const locationHtml = locationLabel
        ? `<div class="freight-result__loc">
            <span class="freight-result__loc-text">
              <svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="8.5"></circle><path d="M6 10.4l2.8 2.8L14 7.8"></path></svg>
              ${escapeHtml(locationLabel)}
            </span>
            <button type="button" class="freight-result__change">${this.i18n?.change_cep || 'Alterar CEP'}</button>
          </div>`
        : '';

      this.resultContainer.innerHTML = `
        <div class="freight-result__card">
          ${locationHtml}
          <div class="freight-result__list">
            ${this.rates.slice(0, visibleCount).map(renderRate).join('')}
            ${moreHtml}
          </div>
        </div>
      `;

      if (this.dataset.totalPreview !== 'false') {
        this.updateTotalsWithFreight(selected.price, selected.name || defaultName);
      }
    }

    // Picking a rate re-prices the total on the spot; the choice outlives drawer re-renders for this CEP.
    selectRate(rate) {
      try {
        sessionStorage.setItem('skafe_shipping_rate', `${this.cleanCep}:${rate.name}`);
      } catch (e) {}
      if (this.dataset.totalPreview !== 'false') {
        this.updateTotalsWithFreight(rate.price, rate.name || this.i18n?.default_method);
      }
    }

    async calculate() {
      const rawVal = this.input ? this.input.value : '';
      const cleanCep = rawVal.replace(/\D/g, '');

      const msgInvalid = this.i18n?.invalid_cep || 'Digite um CEP válido.';
      const msgCalculating = this.i18n?.calculating || 'Calculando frete...';
      const msgNotFound = this.i18n?.not_found || 'Não encontramos uma opção de entrega para este CEP.';
      const msgError = this.i18n?.error || 'Não foi possível calcular agora. Confira o frete no checkout.';
      const defaultName = this.i18n?.default_method || 'Entrega padrão';

      if (cleanCep.length !== 8) {
        this.showStatus(msgInvalid, true);
        if (window.SkafeMinimumOrder) window.SkafeMinimumOrder.refresh();
        return;
      }

      this.cleanCep = cleanCep;

      // Save CEP
      try {
        sessionStorage.setItem('skafe_shipping_cep', cleanCep);
        localStorage.setItem('skafe_shipping_cep', cleanCep);
      } catch (e) {}

      this.showStatus(msgCalculating);
      if (this.button) this.button.disabled = true;
      const calculationId = (this.calculationId || 0) + 1;
      this.calculationId = calculationId;

      try {
        // 1. Resolve location (ViaCEP with fallback)
        let uf = null;
        let city = '';
        try {
          const viaCepRes = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, {
            signal: AbortSignal.timeout(3000),
          });
          if (viaCepRes.ok) {
            const viaCepData = await viaCepRes.json();
            if (!viaCepData.erro) {
              uf = viaCepData.uf;
              city = viaCepData.localidade || '';
            }
          }
        } catch (err) {
          // Fallback to range
        }

        if (!uf) {
          const rangeMatch = getUfFromCep(cleanCep);
          if (rangeMatch) {
            uf = rangeMatch.uf;
            city = rangeMatch.state;
          }
        }

        if (!uf) {
          this.showStatus(msgNotFound, true);
          return;
        }

        const locationLabel = city ? `${city} - ${uf}` : uf;

        // 2. Try Shopify Ajax Cart Shipping Rates
        let rates = null;
        try {
          const prepareUrl = `/cart/prepare_shipping_rates.json`;
          const params = new URLSearchParams({
            'shipping_address[zip]': cleanCep,
            'shipping_address[country]': 'Brazil',
            'shipping_address[province]': uf,
          });

          const prepRes = await fetch(prepareUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
          });

          if (prepRes.ok) {
            // Poll async shipping rates
            const asyncUrl = `/cart/async_shipping_rates.json`;
            // Frenet takes several seconds; Shopify answers 202 until the quotes are in.
            for (let attempt = 0; attempt < 20; attempt++) {
              await new Promise((r) => setTimeout(r, 750));
              const asyncRes = await fetch(asyncUrl);
              if (asyncRes.status === 202) continue;
              if (asyncRes.ok) {
                const asyncData = await asyncRes.json();
                if (!asyncData) continue; // still pending
                if (asyncData.shipping_rates && asyncData.shipping_rates.length > 0) {
                  const plausibleRates = asyncData.shipping_rates
                    .map((r) => ({
                      name: r.name || defaultName,
                      price: parseFloat(r.price),
                      currency: r.currency || 'BRL',
                      days: r.delivery_days,
                    }))
                    .filter((r) => !isNaN(r.price) && r.price > 0 && r.price <= 500);

                  if (plausibleRates.length > 0) {
                    rates = plausibleRates;
                    break;
                  } else {
                    console.warn('[Freight] Carrier returned implausible shipping rate(s) (> R$ 500). Falling back to canonical rates.');
                  }
                }
              }
              break;
            }
          }
        } catch (err) {
          // Fallback to canonical
        }

        // 3. If Shopify Cart API didn't return (e.g. empty cart or rate polling), use canonical rate for UF
        if (!rates || rates.length === 0) {
          const fallbackPrice = this.canonicalRates?.rates?.[uf];
          if (typeof fallbackPrice === 'number' && fallbackPrice > 0) {
            rates = [
              {
                name: this.canonicalRates?.method_name || defaultName,
                price: fallbackPrice,
                currency: 'BRL',
              },
            ];
          }
        }

        if (calculationId !== this.calculationId) return;
        if (rates && rates.length > 0) {
          this.showResult(rates, locationLabel);
        } else {
          this.showStatus(msgNotFound, true);
        }
      } catch (err) {
        if (calculationId !== this.calculationId) return;
        console.error('Error calculating freight:', err);
        this.showStatus(msgError, true);
      } finally {
        if (calculationId === this.calculationId) {
          if (this.button) this.button.disabled = false;
          if (window.SkafeMinimumOrder) window.SkafeMinimumOrder.refresh();
        }
      }
    }
  }

  customElements.define('skafe-freight-calculator', SkafeFreightCalculator);
})();
